import { useParams, Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLead } from '@/hooks/useLeads';
import { usePageTitle } from '@/hooks/usePageTitle';
import { formatDateTime } from '@/utils/format';

export const LeadDetailPage = () => {
  usePageTitle('Lead Details');
  const { leadId } = useParams<{ leadId: string }>();
  const { data: lead, isLoading } = useLead(leadId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Lead detail</p>
            <h1 className="text-3xl font-bold tracking-tight">Loading...</h1>
          </div>
          <Button variant="outline" asChild>
            <Link to="/leads">Back to leads</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Lead detail</p>
            <h1 className="text-3xl font-bold tracking-tight">Not Found</h1>
            <p className="text-muted-foreground">We could not locate lead ID {leadId}.</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/leads">Back to leads</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Lead detail</p>
          <h1 className="text-3xl font-bold tracking-tight">{lead.customer_name}</h1>
          <p className="text-muted-foreground">Created {formatDateTime(lead.created_at)}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/leads">Back to leads</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>Customer details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-muted-foreground text-sm">Customer Name</p>
              <p className="text-sm font-medium">{lead.customer_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Phone Number</p>
              <p className="text-sm font-medium">{lead.phone_number}</p>
            </div>
            {lead.preferred_callback_time && (
              <div>
                <p className="text-muted-foreground text-sm">Preferred Callback Time</p>
                <p className="text-sm font-medium">{lead.preferred_callback_time}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vehicle Interest</CardTitle>
            <CardDescription>Customer preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lead.vehicle_interest && (
              <div>
                <p className="text-muted-foreground text-sm">Vehicle Interest</p>
                <p className="text-sm font-medium">{lead.vehicle_interest}</p>
              </div>
            )}
            {lead.budget && (
              <div>
                <p className="text-muted-foreground text-sm">Budget</p>
                <p className="text-sm font-medium">{lead.budget}</p>
              </div>
            )}
            {lead.finance_preference && (
              <div>
                <p className="text-muted-foreground text-sm">Finance Preference</p>
                <span className="bg-primary/10 text-primary inline-flex items-center rounded-md px-2 py-1 text-xs font-medium">
                  {lead.finance_preference}
                </span>
              </div>
            )}
            {lead.showroom_interest !== null && lead.showroom_interest !== undefined && (
              <div>
                <p className="text-muted-foreground text-sm">Showroom Interest</p>
                <span
                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                    lead.showroom_interest
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {lead.showroom_interest ? 'Yes' : 'No'}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {lead.additional_notes && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
            <CardDescription>Additional information captured</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{lead.additional_notes}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Related Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-muted-foreground text-sm">Campaign</p>
            <p className="text-sm font-medium">
              {lead.campaign ? (
                <Link to={`/campaigns/${lead.campaign.id}`} className="text-primary underline">
                  {lead.campaign.name}
                </Link>
              ) : (
                'N/A'
              )}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">Call</p>
            <p className="text-sm font-medium">
              {lead.call ? (
                <Link to={`/calls/${lead.call_id}`} className="text-primary underline">
                  View Call
                </Link>
              ) : (
                'N/A'
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
